// src/utils/dateUtils.js

/**
 * Format a date into a readable format
 * @param {string|Date} date - The date to format
 * @param {Object} options - Options for date formatting
 * @returns {string} Formatted date string
 */
export const formatDate = (date, options = {}) => {
  if (!date) return "-";

  try {
    const dateObj = typeof date === "string" ? new Date(date) : date;

    if (isNaN(dateObj.getTime())) {
      return "-";
    }

    const defaultOptions = {
      day: "numeric",
      month: "short",
      year: "numeric",
      ...options,
    };

    return dateObj.toLocaleDateString("en-PK", defaultOptions);
  } catch (error) {
    console.error("Error formatting date:", error);
    return "-";
  }
};

/**
 * Format a number as PKR currency
 * @param {number} amount - The amount to format
 * @param {Object} options - Options for currency formatting
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount, options = {}) => {
  if (amount === null || amount === undefined) return "₹0";

  try {
    const numAmount = typeof amount === "string" ? parseFloat(amount) : amount;

    if (isNaN(numAmount)) {
      return "₹0";
    }

    const defaultOptions = {
      style: "currency",
      currency: "PKR",
      currencyDisplay: "symbol",
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
      ...options,
    };

    // Create a formatted string with the PKR currency symbol
    const formatter = new Intl.NumberFormat("en-PK", defaultOptions);

    // Return the formatted amount, replacing the default PKR symbol with ₹
    return formatter.format(numAmount).replace("PKR", "₹");
  } catch (error) {
    console.error("Error formatting currency:", error);
    return "₹0";
  }
};

/**
 * Format a number to display with commas for thousands
 * @param {number} number - The number to format
 * @returns {string} Formatted number string with commas
 */
export const formatNumber = (number) => {
  if (number === null || number === undefined) return "0";

  try {
    const numValue = typeof number === "string" ? parseFloat(number) : number;

    if (isNaN(numValue)) {
      return "0";
    }

    // Format number with Pakistani number formatting (similar to Indian)
    return new Intl.NumberFormat("en-PK").format(numValue);
  } catch (error) {
    console.error("Error formatting number:", error);
    return "0";
  }
};

/**
 * Calculate the difference between two dates in days
 * @param {string|Date} date1 - First date
 * @param {string|Date} date2 - Second date
 * @returns {number} Number of days difference
 */
export const daysBetween = (date1, date2) => {
  try {
    const d1 = new Date(date1);
    const d2 = new Date(date2);

    // Convert to UTC to avoid timezone issues
    const utc1 = Date.UTC(d1.getFullYear(), d1.getMonth(), d1.getDate());
    const utc2 = Date.UTC(d2.getFullYear(), d2.getMonth(), d2.getDate());

    // Calculate difference in milliseconds and convert to days
    const diffDays = Math.floor((utc2 - utc1) / (1000 * 60 * 60 * 24));

    return diffDays;
  } catch (error) {
    console.error("Error calculating days between dates:", error);
    return 0;
  }
};

/**
 * Add days to a date
 * @param {string|Date} date - The base date
 * @param {number} days - Number of days to add
 * @returns {Date} New date
 */
export const addDays = (date, days) => {
  try {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
  } catch (error) {
    console.error("Error adding days to date:", error);
    return new Date();
  }
};

/**
 * Check if a date is in the past
 * @param {string|Date} date - The date to check
 * @returns {boolean} True if the date is in the past
 */
export const isPastDate = (date) => {
  try {
    const checkDate = new Date(date);
    const today = new Date();

    // Remove time component for comparison
    today.setHours(0, 0, 0, 0);
    checkDate.setHours(0, 0, 0, 0);

    return checkDate < today;
  } catch (error) {
    console.error("Error checking if date is in past:", error);
    return false;
  }
};

/**
 * Format a date as YYYY-MM-DD (suitable for input[type="date"])
 * @param {string|Date} date - The date to format
 * @returns {string} Formatted date string
 */
export const formatDateForInput = (date) => {
  if (!date) return "";

  try {
    const d = new Date(date);

    if (isNaN(d.getTime())) {
      return "";
    }

    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  } catch (error) {
    console.error("Error formatting date for input:", error);
    return "";
  }
};
